import { io, Socket } from 'socket.io-client'
import type {
  ConversationNewEvent,
  ConversationMessageEvent,
  ConversationStatusChangedEvent,
} from '@/types/Conversation'

const CHAT_SOCKET_NAMESPACE = '/chat'

const SOCKET_EVENTS = {
  CONNECT: 'connect',
  CONVERSATION_NEW: 'conversation:new',
  CONVERSATION_MESSAGE: 'conversation:message',
  CONVERSATION_STATUS_CHANGED: 'conversation:status_changed',
  AGENT_ENTER_CONVERSATION: 'agent:enter_conversation',
  AGENT_SEND_MESSAGE: 'agent:send_message',
  AGENT_CLOSE_CONVERSATION: 'agent:close_conversation',
  CALL_OFFER: 'call:offer',
  CALL_ANSWER: 'call:answer',
  CALL_MEDIA_CONNECTED: 'call:media-connected',
  CALL_HANGUP: 'call:hangup',
  CALL_REJECT: 'call:reject',
  CALL_TAKEN: 'call:taken',
  CALL_CANCELLED: 'call:cancelled',
  CALL_ENDED: 'call:ended',
} as const

interface AgentEnterPayload {
  messageDocId: string
  contactPhone: string
}

interface AgentSendPayload {
  messageDocId: string
  contactPhone: string
  content: string
}

interface AgentClosePayload {
  messageDocId: string
  contactPhone: string
}

export interface CallOfferEvent {
  callId: string
  sdp: string
  contactPhone: string
  contactName: string
  messageDocId: string | null
}

export interface CallTakenEvent {
  callId: string
}

export interface CallCancelledEvent {
  callId: string
}

export interface CallEndedEvent {
  callId: string
  durationSeconds: number | null
}

interface CallAnswerPayload {
  callId: string
  sdp: string
}

interface CallMediaConnectedPayload {
  callId: string
}

interface CallHangupPayload {
  callId: string
}

interface CallRejectPayload {
  callId: string
}

export type ChatSocketInstance = {
  onConnect: (handler: () => void) => void
  offConnect: (handler: () => void) => void
  onConversationNew: (handler: (event: ConversationNewEvent) => void) => void
  onConversationMessage: (handler: (event: ConversationMessageEvent) => void) => void
  onConversationStatusChanged: (handler: (event: ConversationStatusChangedEvent) => void) => void
  offConversationNew: (handler: (event: ConversationNewEvent) => void) => void
  offConversationMessage: (handler: (event: ConversationMessageEvent) => void) => void
  offConversationStatusChanged: (handler: (event: ConversationStatusChangedEvent) => void) => void
  enterConversation: (payload: AgentEnterPayload) => void
  sendMessage: (payload: AgentSendPayload) => void
  closeConversation: (payload: AgentClosePayload) => void
  onCallOffer: (handler: (event: CallOfferEvent) => void) => void
  offCallOffer: (handler: (event: CallOfferEvent) => void) => void
  onCallTaken: (handler: (event: CallTakenEvent) => void) => void
  offCallTaken: (handler: (event: CallTakenEvent) => void) => void
  onCallCancelled: (handler: (event: CallCancelledEvent) => void) => void
  offCallCancelled: (handler: (event: CallCancelledEvent) => void) => void
  onCallEnded: (handler: (event: CallEndedEvent) => void) => void
  offCallEnded: (handler: (event: CallEndedEvent) => void) => void
  answerCall: (payload: CallAnswerPayload) => void
  notifyCallMediaConnected: (payload: CallMediaConnectedPayload) => void
  hangupCall: (payload: CallHangupPayload) => void
  rejectCall: (payload: CallRejectPayload) => void
  disconnect: () => void
}

export function createChatSocket(): ChatSocketInstance {
  const backendUrl = import.meta.env.VITE_API_URL as string
  const socket: Socket = io(`${backendUrl}${CHAT_SOCKET_NAMESPACE}`, {
    withCredentials: true,
    transports: ['polling', 'websocket'],
  })

  return {
    onConnect(handler) {
      socket.on(SOCKET_EVENTS.CONNECT, handler)
    },
    offConnect(handler) {
      socket.off(SOCKET_EVENTS.CONNECT, handler)
    },
    onConversationNew(handler) {
      socket.on(SOCKET_EVENTS.CONVERSATION_NEW, handler)
    },
    onConversationMessage(handler) {
      socket.on(SOCKET_EVENTS.CONVERSATION_MESSAGE, handler)
    },
    onConversationStatusChanged(handler) {
      socket.on(SOCKET_EVENTS.CONVERSATION_STATUS_CHANGED, handler)
    },
    offConversationNew(handler) {
      socket.off(SOCKET_EVENTS.CONVERSATION_NEW, handler)
    },
    offConversationMessage(handler) {
      socket.off(SOCKET_EVENTS.CONVERSATION_MESSAGE, handler)
    },
    offConversationStatusChanged(handler) {
      socket.off(SOCKET_EVENTS.CONVERSATION_STATUS_CHANGED, handler)
    },
    enterConversation(payload) {
      socket.emit(SOCKET_EVENTS.AGENT_ENTER_CONVERSATION, payload)
    },
    sendMessage(payload) {
      socket.emit(SOCKET_EVENTS.AGENT_SEND_MESSAGE, payload)
    },
    closeConversation(payload) {
      socket.emit(SOCKET_EVENTS.AGENT_CLOSE_CONVERSATION, payload)
    },
    onCallOffer(handler) {
      socket.on(SOCKET_EVENTS.CALL_OFFER, handler)
    },
    offCallOffer(handler) {
      socket.off(SOCKET_EVENTS.CALL_OFFER, handler)
    },
    onCallTaken(handler) {
      socket.on(SOCKET_EVENTS.CALL_TAKEN, handler)
    },
    offCallTaken(handler) {
      socket.off(SOCKET_EVENTS.CALL_TAKEN, handler)
    },
    onCallCancelled(handler) {
      socket.on(SOCKET_EVENTS.CALL_CANCELLED, handler)
    },
    offCallCancelled(handler) {
      socket.off(SOCKET_EVENTS.CALL_CANCELLED, handler)
    },
    onCallEnded(handler) {
      socket.on(SOCKET_EVENTS.CALL_ENDED, handler)
    },
    offCallEnded(handler) {
      socket.off(SOCKET_EVENTS.CALL_ENDED, handler)
    },
    answerCall(payload) {
      socket.emit(SOCKET_EVENTS.CALL_ANSWER, payload)
    },
    notifyCallMediaConnected(payload) {
      socket.emit(SOCKET_EVENTS.CALL_MEDIA_CONNECTED, payload)
    },
    hangupCall(payload) {
      socket.emit(SOCKET_EVENTS.CALL_HANGUP, payload)
    },
    rejectCall(payload) {
      socket.emit(SOCKET_EVENTS.CALL_REJECT, payload)
    },
    disconnect() {
      socket.disconnect()
    },
  }
}
